import { useEffect } from 'react';

/**
 * Global keyboard shortcuts.
 *
 * Two rules keep them from fighting the app:
 *
 * 1. Nothing fires while the user is typing (input, textarea, select or any
 *    contenteditable) unless the binding explicitly asks for it — a bare `c` must
 *    not create a chart in the middle of a sentence.
 * 2. Every binding here is also reachable by mouse. A shortcut is an accelerator,
 *    never the only route (the reason `?` opens a dialog that lists them all).
 */

export interface Shortcut {
  /** Lowercase key, as `KeyboardEvent.key`. */
  key: string;
  meta?: boolean;
  shift?: boolean;
  /** Allow the binding to fire while a text field has focus. */
  whileTyping?: boolean;
  run: (event: KeyboardEvent) => void;
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

export function useShortcuts(shortcuts: Shortcut[], enabled = true): void {
  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      const meta = event.metaKey || event.ctrlKey;
      for (const s of shortcuts) {
        if (s.key !== key) continue;
        if (Boolean(s.meta) !== meta) continue;
        if (s.shift !== undefined && s.shift !== event.shiftKey) continue;
        if (!s.whileTyping && isTypingTarget(event.target)) continue;
        event.preventDefault();
        s.run(event);
        return;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [shortcuts, enabled]);
}

/** What the shortcuts dialog lists. Kept beside the bindings so it cannot drift. */
export const SHORTCUT_HELP: Array<{ group: string; items: Array<{ keys: string[]; label: string }> }> = [
  {
    group: 'Anywhere',
    items: [
      { keys: ['⌘', 'K'], label: 'Search charts, dashboards and data sources' },
      { keys: ['⌘', 'J'], label: 'Show or hide the assistant' },
      { keys: ['?'], label: 'Keyboard shortcuts' },
      { keys: ['⌘', 'B'], label: 'Collapse or expand the sidebar' },
    ],
  },
  {
    group: 'Create & navigate',
    items: [
      { keys: ['C'], label: 'New chart' },
      { keys: ['D'], label: 'New dashboard' },
      { keys: ['G', 'H'], label: 'Go to Home' },
      { keys: ['G', 'C'], label: 'Go to Charts' },
      { keys: ['G', 'D'], label: 'Go to Dashboards' },
      { keys: ['G', 'S'], label: 'Go to Data sources' },
    ],
  },
  {
    group: 'Chart builder',
    items: [
      { keys: ['⌘', 'S'], label: 'Save the chart' },
      { keys: ['⌘', 'Enter'], label: 'Send to the assistant' },
      { keys: ['Esc'], label: 'Leave full-screen preview' },
    ],
  },
];
